import { useEffect, useMemo, useRef, useState } from "react";
import AppLayout, { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtMoney, fmtMoneyCompact, fmtNumber, fmtMonth, fmtDate } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Wallet, Receipt, TrendingUp, Banknote, PieChart as PieIcon, FileUp, Volume2, VolumeX, FileText } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCompactMoney } from "@/lib/useCompactMoney";
import CompactMoneyToggle from "@/components/CompactMoneyToggle";
import { useAuth } from "@/lib/auth";

const EXPENSE_PALETTE = [
  "#1B4332", "#1B4332", "#60A5FA", "#1E40AF", "#7C3AED",
  "#0891B2", "#DC2626", "#D97706", "#059669", "#94A3B8",
];

// Saturated ("solid") card backgrounds — randomized on each mount for variety.
// Kept bright enough that dark text stays readable; each entry pairs the card
// tint with a matching darker accent used on the top strip.
const KPI_BG_PALETTE = [
  { tint: "#6EE7B7", accent: "#047857" }, // emerald-300 / emerald-700
  { tint: "#FCD34D", accent: "#B45309" }, // amber-300 / amber-700
  { tint: "#7DD3FC", accent: "#0369A1" }, // sky-300 / sky-700
  { tint: "#FDA4AF", accent: "#BE123C" }, // rose-300 / rose-700
  { tint: "#C4B5FD", accent: "#6D28D9" }, // violet-300 / violet-700
  { tint: "#FDBA74", accent: "#C2410C" }, // orange-300 / orange-700
  { tint: "#A5B4FC", accent: "#4338CA" }, // indigo-300 / indigo-700
  { tint: "#5EEAD4", accent: "#0F766E" }, // teal-300 / teal-700
];

// Fisher-Yates shuffle — returns a new array so we can pick N distinct pairs.
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const KPI = ({ icon: Icon, label, value, hint, testid, tint, accent, to, delta, secondaryLinks }) => {
  const deltaPct = delta?.pct;
  const deltaPositive = typeof deltaPct === "number" && deltaPct > 0;
  const deltaNegative = typeof deltaPct === "number" && deltaPct < 0;
  const goodIsUp = !delta?.invertColor;
  const showGreen = (goodIsUp && deltaPositive) || (!goodIsUp && deltaNegative);
  const showRed = (goodIsUp && deltaNegative) || (!goodIsUp && deltaPositive);
  const deltaColor = showGreen ? "var(--ll-success)" : showRed ? "var(--ll-error)" : "var(--ll-ink-soft)";

  const inner = (
    <>
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[var(--ll-ink-muted)] truncate pr-2">{label}</div>
        <Icon size={14} className="opacity-60 shrink-0" style={accent ? { color: accent } : undefined} />
      </div>
      <div className="font-mono-fin tracking-tight text-lg sm:text-2xl xl:text-3xl break-words leading-tight" style={{ fontSize: "clamp(0.95rem, 2.2vw, 1.875rem)" }}>{value}</div>
      <div className="text-[11px] sm:text-xs text-[var(--ll-ink-soft)] mt-1 flex items-center gap-1.5 sm:gap-2 flex-wrap">
        {delta && deltaPct !== null && deltaPct !== undefined ? (
          <span
            className="inline-flex items-center gap-0.5 font-mono-fin font-medium px-1.5 py-0.5"
            style={{ color: deltaColor, background: showGreen ? "#DCFCE7" : showRed ? "#FEE2E2" : "rgba(11,37,69,0.05)" }}
            data-testid={`${testid}-delta`}
          >
            {deltaPositive ? <ArrowUpRight size={10} /> : deltaNegative ? <ArrowDownRight size={10} /> : null}
            {Math.abs(deltaPct).toFixed(1)}%
          </span>
        ) : delta && deltaPct === null ? (
          <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]" data-testid={`${testid}-delta-new`}>new</span>
        ) : null}
        <span className="truncate hidden sm:inline">{delta ? "vs prior 30d" : hint}</span>
        <span className="truncate sm:hidden text-[10px]">{delta ? "vs prior 30d" : hint}</span>
      </div>
      {to && !secondaryLinks && (
        <div
          className="mt-3 pt-3 border-t text-[10px] uppercase tracking-[0.2em] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ borderColor: "rgba(11,37,69,0.12)", color: accent || "var(--ll-ink)" }}
        >
          View detail <ArrowUpRight size={11} />
        </div>
      )}
    </>
  );
  const cardClass = `ll-card p-3 sm:p-6 group block transition-transform duration-200 ${to && !secondaryLinks ? "hover:-translate-y-0.5 cursor-pointer" : ""}`;

  const cardStyle = { backgroundColor: tint || "#FFFFFF", borderTop: `3px solid ${accent || "var(--ll-ink)"}` };

  // Card with secondary drill-down links — render as div with multiple Links inside
  if (secondaryLinks) {
    return (
      <div className={`ll-card p-3 sm:p-6 group block transition-transform duration-200 ${secondaryLinks.length > 0 ? "hover:-translate-y-0.5" : ""}`}
           data-testid={testid}
           style={cardStyle}>
        {inner}
        <div
          className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2"
          style={{ borderColor: "rgba(11,37,69,0.12)" }}
          data-testid={`${testid}-drill-links`}
        >
          {secondaryLinks.map((lnk) => (
            <Link
              key={lnk.to}
              to={lnk.to}
              data-testid={lnk.testid}
              className="text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 px-2 py-1 border hover:bg-white transition-colors"
              style={{
                borderColor: accent || "var(--ll-ink)",
                color: accent || "var(--ll-ink)",
                background: "rgba(255,255,255,0.4)",
              }}
            >
              {lnk.label} <ArrowUpRight size={10} />
            </Link>
          ))}
        </div>
      </div>
    );
  }

  if (to) {
    return (
      <Link
        to={to}
        className={cardClass}
        data-testid={testid}
        style={cardStyle}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className={cardClass} data-testid={testid} style={cardStyle}>
      {inner}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const canCreateInvoice = ["owner", "admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [expenses, setExpenses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientUploads, setClientUploads] = useState([]);
  const seenUploadIds = useRef(null); // Set of ids seen since page mount; null on first fetch
  const navigate = useNavigate();

  // Per-user "shush the chime" — no backend needed since it's a personal
  // preference. Persisted so refreshes remember.
  const [muteChime, setMuteChime] = useState(() => {
    try { return localStorage.getItem("dashboard_mute_chime") === "1"; } catch { return false; }
  });
  const toggleMuteChime = () => {
    setMuteChime((v) => {
      const next = !v;
      try { localStorage.setItem("dashboard_mute_chime", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // Compact-number mode for KPI cards — shared across Dashboard, Reports,
  // and Reconciliation via useCompactMoney().
  const { money: kpiMoney } = useCompactMoney();

  // Play a soft two-tone chime via WebAudio. First mount doesn't chime —
  // only when a NEW id shows up after the initial fetch. Silent on failure
  // (autoplay blocked, no audio context, muted by user, etc.).
  const playChime = () => {
    if (muteChime) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      [523.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const start = now + i * 0.15;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.08, start + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.45);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.5);
      });
      setTimeout(() => ctx.close(), 700);
    } catch { /* silent */ }
  };

  // Pick 4 distinct random tint/accent pairs once per mount so every visit to
  // the dashboard reshuffles the KPI card backgrounds.
  const kpiColors = useMemo(() => shuffle(KPI_BG_PALETTE).slice(0, 4), []);

  const loadClientUploads = async () => {
    try {
      const r = await api.get("/statements/client-uploads", { params: { limit: 12 } });
      const items = r.data.items || [];
      // Compare ids to detect NEW arrivals since last poll. The very first
      // fetch (`seenUploadIds.current === null`) is silent — we don't chime
      // on page load, only on actual new uploads.
      const currentIds = new Set(items.map((s) => s.id));
      if (seenUploadIds.current !== null) {
        const isFresh = items.some((s) => s.is_new && !seenUploadIds.current.has(s.id));
        if (isFresh) playChime();
      }
      seenUploadIds.current = currentIds;
      setClientUploads(items);
    } catch { /* silent — no client uploads is fine */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [k, tx, ex] = await Promise.all([
          api.get("/reports/dashboard"),
          api.get("/transactions", { params: { limit: 8 } }),
          api.get("/reports/expenses"),
        ]);
        setData(k.data);
        setRecent(tx.data || []);
        setExpenses(ex.data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
    loadClientUploads();
    // Auto-refresh client uploads every 20s so the beacon starts blinking
    // without a manual reload.
    const id = setInterval(loadClientUploads, 20000);
    return () => clearInterval(id);
  }, []);

  const openClientUpload = async (s) => {
    // Mark reviewed (kills the beacon) then jump to Statements filtered to
    // this statement's bank account.
    try {
      if (s.is_new) await api.post(`/statements/${s.id}/mark-reviewed`);
    } catch { /* ignore */ }
    navigate(`/app/statements?bank=${encodeURIComponent(s.bank_account_id)}`);
  };

  // Top 8 categories + "Other" bucket
  const expensePie = (() => {
    if (!expenses?.by_category?.length) return [];
    const N = 8;
    const sorted = [...expenses.by_category].sort((a, b) => b.amount - a.amount);
    if (sorted.length <= N) return sorted;
    const top = sorted.slice(0, N);
    const restAmt = sorted.slice(N).reduce((s, x) => s + x.amount, 0);
    const restPct = sorted.slice(N).reduce((s, x) => s + x.pct, 0);
    return [...top, { category: "Other", amount: restAmt, pct: restPct }];
  })();

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Control room"
        testid="dashboard-header"
        actions={
          <>
            <CompactMoneyToggle testid="dashboard-compact-toggle" />
            {canCreateInvoice && (
              <Link to="/app/invoices" className="text-sm px-4 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5" style={{ borderColor: "var(--ll-border)" }} data-testid="dashboard-invoices-cta">
                <FileText size={14} /> Create invoice
              </Link>
            )}
            <Link to="/app/statements" className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] transition-colors" data-testid="dashboard-upload-cta">
              Upload statement
            </Link>
          </>
        }
      />
      <PageBody>
        {loading && <div className="ll-progress mb-6" />}
        <div className={`grid grid-cols-2 sm:grid-cols-2 ${canCreateInvoice && (data?.accounts_receivable?.total || 0) > 0 ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 sm:gap-4 mb-6 sm:mb-8`}>
          <KPI icon={TrendingUp} label="Total Revenue" testid="kpi-revenue"
               tint={kpiColors[0].tint} accent={kpiColors[0].accent}
               to="/app/transactions?type=income"
               delta={data?.deltas?.revenue}
               value={kpiMoney(data?.total_revenue || 0)}
               hint="All-time, parsed transactions" />
          <KPI icon={Receipt} label="Total Expenses" testid="kpi-expenses"
               tint={kpiColors[1].tint} accent={kpiColors[1].accent}
               to="/app/transactions?type=expense"
               delta={data?.deltas?.expenses ? { ...data.deltas.expenses, invertColor: true } : undefined}
               value={kpiMoney(data?.total_expenses || 0)}
               hint="Includes COGS and operating" />
          <KPI icon={ArrowUpRight} label="Net Profit" testid="kpi-net-profit"
               tint={kpiColors[2].tint} accent={kpiColors[2].accent}
               secondaryLinks={[
                 { to: "/app/reports?tab=pnl", label: "P&L", testid: "kpi-net-profit-pnl" },
                 { to: "/app/reports?tab=cf", label: "Cash Flow", testid: "kpi-net-profit-cf" },
               ]}
               delta={data?.deltas?.net_profit}
               value={kpiMoney(data?.net_profit || 0)}
               hint={data?.net_profit >= 0 ? "Positive cash flow" : "Loss for period"} />
          <KPI icon={Wallet} label="Cash Position" testid="kpi-cash"
               tint={kpiColors[3].tint} accent={kpiColors[3].accent}
               to="/app/reconciliation"
               value={kpiMoney(data?.cash_position || 0)}
               hint={`${fmtNumber(data?.bank_account_count || 0)} bank account(s)`} />
          {canCreateInvoice && (data?.accounts_receivable?.total || 0) > 0 && (
            <KPI icon={FileText} label="Accounts Receivable" testid="kpi-ar"
                 tint="#FEF3C7" accent="#B45309"
                 to="/app/reports?tab=ar"
                 value={kpiMoney(data?.accounts_receivable?.total || 0)}
                 hint={`${(data?.accounts_receivable?.overdue_pct || 0).toFixed(0)}% overdue · oldest ${data?.accounts_receivable?.oldest_days || 0}d`} />
          )}
        </div>

        {/* Recent client uploads — with a blinking beacon on new arrivals. */}
        {clientUploads.length > 0 && (
          <div className="ll-card-flat p-6 mb-4" data-testid="client-uploads-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Client activity</div>
                <div className="font-display font-bold text-xl flex items-center gap-2">
                  Recent client uploads
                  {clientUploads.some((s) => s.is_new) && (
                    <span
                      className="relative inline-flex items-center justify-center w-3 h-3"
                      aria-label="New uploads available"
                      data-testid="client-uploads-beacon"
                    >
                      <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                      <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500"></span>
                    </span>
                  )}
                </div>
              </div>
              <Link
                to="/app/statements"
                className="text-xs text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
                data-testid="client-uploads-see-all"
              >
                See all →
              </Link>
            </div>
            <div className="flex items-center justify-between mb-2 text-[11px] text-[var(--ll-ink-muted)]">
              <span>Chime plays when a new upload lands</span>
              <button
                onClick={toggleMuteChime}
                data-testid="dashboard-mute-chime-toggle"
                className="hover:text-[var(--ll-ink)] flex items-center gap-1"
                aria-pressed={muteChime}
              >
                {muteChime ? <><VolumeX size={12} /> Muted · click to unmute</> : <><Volume2 size={12} /> Mute chime</>}
              </button>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
              {clientUploads.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openClientUpload(s)}
                  data-testid={`client-upload-row-${s.id}`}
                  className="w-full text-left px-1 py-3 flex items-start gap-3 hover:bg-[var(--ll-bg-sidebar)] transition-colors"
                >
                  <div className="relative w-9 h-9 shrink-0 grid place-items-center bg-[var(--ll-bg-sidebar)]">
                    <FileUp size={16} className="text-[var(--ll-ink-muted)]" />
                    {s.is_new && (
                      <span
                        className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-2.5 h-2.5"
                        aria-label="New"
                        data-testid={`client-upload-beacon-${s.id}`}
                      >
                        <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-medium truncate">{s.client_name || s.client_email}</div>
                      <span
                        className="text-[10px] uppercase tracking-widest px-1.5 py-0.5"
                        style={{
                          background: s.status === "parsed" ? "#D1FAE5" : s.status === "failed" ? "#FEE2E2" : "#DBEAFE",
                          color: s.status === "parsed" ? "#065F46" : s.status === "failed" ? "#991B1B" : "#1E3A8A",
                        }}
                      >{s.status === "processing" ? "Parsing" : s.status}</span>
                    </div>
                    <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5 truncate">
                      {s.file_name}
                      {s.bank_account_name && <> · {s.bank_account_name}</>}
                      {s.status === "parsed" && s.transaction_count ? <> · <strong>{s.transaction_count}</strong> txns</> : null}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-1">{fmtDate(s.uploaded_at)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
          <div className="ll-card-flat p-4 sm:p-6 lg:col-span-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Cash flow</div>
                <div className="font-display font-bold text-lg sm:text-xl">Income vs Expenses</div>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: "var(--ll-ink)" }} /> Income</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: "var(--ll-ochre)" }} /> Expense</span>
              </div>
            </div>
            <div className="h-56 sm:h-72" data-testid="trend-chart">
              <ResponsiveContainer>
                <BarChart data={data?.trend || []} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#475569", fontFamily: "IBM Plex Mono" }} tickFormatter={(m) => fmtMonth(m)} />
                  <YAxis tick={{ fontSize: 11, fill: "#475569", fontFamily: "IBM Plex Mono" }} />
                  <Tooltip
                    cursor={{ fill: "rgba(11,37,69,0.05)" }}
                    contentStyle={{ background: "#fff", border: "1px solid #1B4332", borderRadius: 0, fontSize: 12, fontFamily: "IBM Plex Mono" }}
                    formatter={(v) => fmtMoney(v)}
                    labelFormatter={(m) => fmtMonth(m)}
                  />
                  <Bar dataKey="income" fill="#1B4332" radius={0} />
                  <Bar dataKey="expense" fill="#DC2626" radius={0} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="ll-card-flat p-4 sm:p-6 lg:col-span-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Activity</div>
                <div className="font-display font-bold text-lg sm:text-xl">Recent transactions</div>
              </div>
              <Link to="/app/transactions" className="text-xs hover:underline">View all</Link>
            </div>
            {recent.length === 0 && (
              <div className="text-sm text-[var(--ll-ink-soft)] py-8 text-center" data-testid="recent-empty">
                <Banknote size={28} className="mx-auto mb-2 opacity-40" />
                No transactions yet. <br />
                <Link to="/app/statements" className="underline">Upload a statement</Link> to get started.
              </div>
            )}
            <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
              {recent.map((t) => (
                <li key={t.id} className="py-3 flex items-center justify-between gap-3" data-testid={`recent-tx-${t.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{t.description}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-0.5">{fmtDate(t.date)} · {t.category}</div>
                  </div>
                  <div className={`font-mono-fin text-sm shrink-0 ${t.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]"}`}>
                    {t.amount < 0 ? <ArrowDownRight size={12} className="inline mr-1" /> : <ArrowUpRight size={12} className="inline mr-1" />}
                    {fmtMoney(t.amount)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Top expense categories */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4" data-testid="expense-pie-section">
          <div className="ll-card-flat p-6 lg:col-span-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Spend breakdown</div>
                <div className="font-display font-bold text-xl">Top expense categories</div>
              </div>
              <PieIcon size={16} className="opacity-40" />
            </div>
            <div className="h-72" data-testid="expense-pie-chart">
              {expensePie.length === 0 ? (
                <div className="h-full grid place-items-center text-sm text-[var(--ll-ink-soft)]" data-testid="expense-pie-empty">
                  No expense data yet.
                </div>
              ) : (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={expensePie}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={110}
                      innerRadius={55}
                      paddingAngle={1}
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      labelLine={false}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                        if (percent < 0.07) return null;
                        const RAD = Math.PI / 180;
                        const r = innerRadius + (outerRadius - innerRadius) * 0.55;
                        const x = cx + r * Math.cos(-midAngle * RAD);
                        const y = cy + r * Math.sin(-midAngle * RAD);
                        return (
                          <text
                            x={x}
                            y={y}
                            fill="#FFFFFF"
                            textAnchor="middle"
                            dominantBaseline="central"
                            style={{ fontFamily: "IBM Plex Mono", fontSize: 12, fontWeight: 700, letterSpacing: "-0.02em" }}
                          >
                            {(percent * 100).toFixed(0)}%
                          </text>
                        );
                      }}
                    >
                      {expensePie.map((_, i) => (
                        <Cell key={i} fill={EXPENSE_PALETTE[i % EXPENSE_PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #1B4332", borderRadius: 0, fontSize: 12, fontFamily: "IBM Plex Mono" }}
                      formatter={(v, _name, props) => [`${fmtMoney(v)} (${props.payload.pct.toFixed(1)}%)`, props.payload.category]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            {expensePie.length > 0 && (
              <div className="text-center text-xs text-[var(--ll-ink-soft)] mt-2">
                <span className="font-mono-fin text-[var(--ll-ink)] font-medium">{fmtMoney(expenses?.total || 0)}</span> total expenses
              </div>
            )}
          </div>

          <div className="ll-card-flat p-6 lg:col-span-7">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Detail</div>
                <div className="font-display font-bold text-xl">By category</div>
              </div>
              <Link to="/app/reports" className="text-xs hover:underline">Full report</Link>
            </div>
            {expensePie.length === 0 ? (
              <div className="h-64 grid place-items-center text-sm text-[var(--ll-ink-soft)]" data-testid="expense-breakdown-empty">
                Upload a statement to see your spend categorized.
              </div>
            ) : (
              <ul className="space-y-3" data-testid="expense-breakdown-list">
                {expensePie.map((c, i) => {
                  const color = EXPENSE_PALETTE[i % EXPENSE_PALETTE.length];
                  return (
                    <li key={c.category} className="flex items-center gap-3" data-testid={`expense-item-${i}`}>
                      <span
                        className="font-mono-fin text-[11px] font-semibold px-2 py-1 shrink-0 text-white"
                        style={{ background: color, minWidth: 56, textAlign: "center" }}
                        data-testid={`expense-pct-${i}`}
                      >
                        {c.pct.toFixed(1)}%
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 text-sm mb-1">
                          <span className="truncate font-medium">{c.category}</span>
                          <span className="font-mono-fin text-[var(--ll-ink-soft)] shrink-0">{fmtMoney(c.amount)}</span>
                        </div>
                        <div className="relative h-1.5" style={{ background: "var(--ll-border)" }}>
                          <div
                            className="absolute inset-y-0 left-0 transition-all"
                            style={{
                              width: `${Math.min(100, c.pct)}%`,
                              background: color,
                            }}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Cumulative cash position */}
        <div className="ll-card-flat p-6" data-testid="cumulative-cash-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Cumulative position</div>
              <div className="font-display font-bold text-xl">Cash over time</div>
            </div>
            <div className="text-xs text-[var(--ll-ink-soft)]">
              Running total across all bank accounts
            </div>
          </div>
          <div className="h-72">
            {(data?.cumulative_cash || []).length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-[var(--ll-ink-soft)]" data-testid="cumulative-cash-empty">
                No data yet — upload a statement to see your cumulative cash position.
              </div>
            ) : (
              <ResponsiveContainer>
                <AreaChart data={data.cumulative_cash} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="cashGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1B4332" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#1B4332" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#475569", fontFamily: "IBM Plex Mono" }}
                    tickFormatter={(d) => fmtDate(d).split(",")[0]}
                    minTickGap={40}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#475569", fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #1B4332", borderRadius: 0, fontSize: 12, fontFamily: "IBM Plex Mono" }}
                    formatter={(v) => [fmtMoney(v), "Cash"]}
                    labelFormatter={(d) => fmtDate(d)}
                  />
                  <Area type="monotone" dataKey="cash" stroke="#1B4332" strokeWidth={2} fill="url(#cashGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </PageBody>
    </>
  );
}
